// Central content configuration for all De Jure sites.
// Transparent, cropped logos served from /public/logos.
export const LOGOS = {
  group: "/logos/group.png",
  legal: "/logos/legal.png",
  marketing: "/logos/marketing.png",
  "corporate-services": "/logos/corporate-services.png",
  consulting: "/logos/consulting.png",
};

// White (monochrome) transparent logos for use on dark surfaces (footer).
export const LOGOS_WHITE = {
  group: "/logos/group-white.png",
  legal: "/logos/legal-white.png",
  marketing: "/logos/marketing-white.png",
  "corporate-services": "/logos/corporate-services-white.png",
  consulting: "/logos/consulting-white.png",
};

export const VIDEOS = {
  group: { src: "/videos/group.mp4", poster: "/videos/group.jpg" },
  legal: { src: "/videos/legal.mp4", poster: "/videos/legal.jpg" },
  marketing: { src: "/videos/marketing.mp4", poster: "/videos/marketing.jpg" },
  "corporate-services": { src: "/videos/corporate.mp4", poster: "/videos/corporate.jpg" },
  consulting: { src: "/videos/consulting.mp4", poster: "/videos/consulting.jpg" },
};

export const GATEWAY = {
  hero: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxkdWJhaSUyMHNreWxpbmUlMjBsdXh1cnklMjBidXNpbmVzc3xlbnwwfHx8fDE3ODUxNTYxMDJ8MA&ixlib=rb-4.1.0&q=85",
  eyebrow: "Est. Dubai, United Arab Emirates",
  titleLines: ["A House of", "Professional", "Excellence"],
  intro:
    "De Jure Group unites a portfolio of specialised advisory practices, legal, corporate, marketing and consulting, under one standard of uncompromising quality. Four disciplines. One trusted name.",
};

export const BRAND_LIST = [
  { key: "legal", name: "De Jure Legal", short: "Legal", to: "/legal", blurb: "Proactive counsel across every jurisdiction." },
  { key: "corporate-services", name: "De Jure Corporate Services", short: "Corporate Services", to: "/corporate-services", blurb: "Company formation, compliance & growth in the UAE." },
  { key: "marketing", name: "De Jure Marketing", short: "Marketing", to: "/marketing", blurb: "Brand, story and market presence with intent." },
  { key: "consulting", name: "De Jure Consulting", short: "Consulting", to: "/consulting", blurb: "Strategy and transformation for ambitious enterprises." },
];

export const BRANDS = {
  legal: {
    key: "legal",
    name: "De Jure Legal",
    to: "/legal",
    logo: LOGOS.legal,
    hero: "https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjb3Jwb3JhdGUlMjBwZW9wbGUlMjBtZWV0aW5nfGVufDB8fHx8MTc4NTE1NjEwMnww&ixlib=rb-4.1.0&q=85",
    eyebrow: "Legal Advisory",
    titleLines: ["The Legal Advisors", "You Always Want", "By Your Side"],
    heroSub:
      "A proactive approach rather than a reactive one, preventing disputes before they arise, and protecting what you have built.",
    about: {
      heading: "Who we are",
      body: "De Jure aspires to revolutionise the legal industry by promoting a proactive attitude towards legal services. Rather than adopting a reactive approach to legal conflicts, the firm encourages its clients to adopt cost-efficient, preemptive measures to prevent potential contentious disputes from arising.",
      body2: "Our ambition is to establish long-lasting relationships with our clients and become a trusted advisor on all legal matters, every step of the way.",
    },
    servicesTitle: "Practice Areas",
    services: [
      { t: "Corporate", d: "Documentation, Drafting and Company Structuring built to endure." },
      { t: "Real Estate", d: "Acquisitions, leasing and development counsel across the region." },
      { t: "Private Client Planning", d: "Wealth, succession and employment matters handled with discretion." },
      { t: "Dispute Management", d: "Strategic resolution, from negotiation to arbitration and litigation." },
      { t: "Wills Management", d: "Estate planning and testamentary structures that protect legacy." },
      { t: "Shipping & Trading Commodities", d: "Maritime, logistics and commodities trade advisory." },
      { t: "Intellectual Property", d: "Protection, registration and enforcement of your assets." },
      { t: "Banking & Finance", d: "Transactional and regulatory guidance for financial matters." },
    ],
    philosophyTitle: "Our Strategy",
    philosophy: [
      { n: "01", t: "Prevention over reaction", d: "We instil a new mindset, prioritising foresight and preemptive protection over costly conflict." },
      { n: "02", t: "Trusted advisory", d: "We become an extension of your team, counselling on every material decision before it becomes a risk." },
      { n: "03", t: "Enduring relationships", d: "Our measure of success is the length and depth of the partnerships we keep." },
    ],
    marquee: ["Corporate", "Real Estate", "Disputes", "Private Client", "Intellectual Property", "Banking & Finance"],
    accent: "#3D2680",
  },
  "corporate-services": {
    key: "corporate-services",
    name: "De Jure Corporate Services",
    to: "/corporate-services",
    logo: LOGOS["corporate-services"],
    hero: "https://images.unsplash.com/photo-1518684079-3c830dcef090?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxkdWJhaSUyMHNreWxpbmUlMjBsdXh1cnklMjBidXNpbmVzc3xlbnwwfHx8fDE3ODUxNTYxMDJ8MA&ixlib=rb-4.1.0&q=85",
    eyebrow: "Corporate Services",
    titleLines: ["Assisting Businesses", "That Want to", "Achieve Growth"],
    heroSub:
      "The corporate advisory team you want by your side every step of the way, from initial setup through ongoing operations in one of the world's fastest-growing economies.",
    about: {
      heading: "Who we are",
      body: "De Jure Corporate Services was established to support businesses navigating expansion within the UAE. We provide a full suite of corporate solutions, company formations, accounting and bookkeeping, compliance and risk management, and detailed financial analysis.",
      body2: "From initial setup through ongoing operations, we ensure businesses remain compliant, financially transparent, and strategically positioned for sustainable growth.",
    },
    servicesTitle: "How we help you",
    services: [
      { t: "Company Formations", d: "Establishing new companies, mainland, free zone and offshore." },
      { t: "Trusts & Foundation Services", d: "Structuring trusts and foundations for protection and continuity." },
      { t: "Compliance & Risk Management", d: "Ensuring you adhere to regulation while managing risk effectively." },
      { t: "Corporate Banking Assistance", d: "Guiding you through account opening and banking relationships." },
      { t: "Document Clearing Services", d: "Facilitating the clearing of essential government documentation." },
      { t: "Accounting & Bookkeeping", d: "Transparent financials and reporting to keep you audit-ready." },
    ],
    philosophyTitle: "Our Values",
    philosophy: [
      { n: "01", t: "Integrity & Transparency", d: "Clear counsel and honest financials at every stage of your journey." },
      { n: "02", t: "Expertise", d: "Deep regional knowledge that turns complexity into a competitive edge." },
      { n: "03", t: "Client-centric approach", d: "We manage the regulatory burden so you can focus on your core operations." },
    ],
    marquee: ["Company Formation", "Compliance", "Banking", "Foundations", "Bookkeeping", "Risk Management"],
    accent: "#3D2680",
  },
  marketing: {
    key: "marketing",
    name: "De Jure Marketing",
    to: "/marketing",
    logo: LOGOS.marketing,
    hero: "https://images.unsplash.com/photo-1768979121229-392fce4957ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwYWJzdHJhY3QlMjBzaWx2ZXIlMjBwdXJwbGUlMjBhcnR8ZW58MHx8fHwxNzg1MTU2MTAyfDA&ixlib=rb-4.1.0&q=85",
    eyebrow: "Brand & Marketing",
    titleLines: ["Presence Built", "With Intent,", "Not Noise"],
    heroSub:
      "A marketing practice for brands that refuse to be forgettable. We craft identity, narrative and demand with the restraint of a luxury house.",
    about: {
      heading: "Who we are",
      body: "De Jure Marketing partners with discerning organisations to build brands worthy of their ambition. We treat marketing as an act of precision, every touchpoint deliberate, every message earning its place.",
      body2: "From identity systems to full-funnel campaigns, we align creative craft with commercial outcomes, so that visibility always translates into value.",
    },
    servicesTitle: "What we do",
    services: [
      { t: "Brand Strategy & Identity", d: "Positioning, naming and visual systems that command attention." },
      { t: "Digital & Performance", d: "Full-funnel campaigns engineered for measurable growth." },
      { t: "Content & Storytelling", d: "Editorial, film and social narratives that carry your voice." },
      { t: "Web & Experience Design", d: "Considered digital experiences that convert and endure." },
      { t: "Social & Community", d: "Building audiences that become advocates for your brand." },
      { t: "Public Relations", d: "Reputation, media and thought-leadership handled with care." },
    ],
    philosophyTitle: "Our Approach",
    philosophy: [
      { n: "01", t: "Clarity before creativity", d: "We define the strategic truth first, the idea only earns its place once the objective is clear." },
      { n: "02", t: "Craft as a standard", d: "Luxury is in the detail. Nothing leaves our studio that we would not be proud to sign." },
      { n: "03", t: "Outcomes that compound", d: "We build brand equity and demand together, so growth accelerates over time." },
    ],
    marquee: ["Brand", "Strategy", "Content", "Performance", "Design", "Public Relations"],
    accent: "#3D2680",
  },
  consulting: {
    key: "consulting",
    name: "De Jure Consulting",
    to: "/consulting",
    logo: LOGOS.consulting,
    hero: "https://images.pexels.com/photos/35058546/pexels-photo-35058546.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    eyebrow: "Strategy & Consulting",
    titleLines: ["Strategy for", "Enterprises That", "Refuse to Settle"],
    heroSub:
      "We advise leadership on the decisions that define their next decade, from strategy and operations to transformation and market entry.",
    about: {
      heading: "Who we are",
      body: "De Jure Consulting is a high-conviction advisory practice for organisations at an inflection point. We combine rigorous analysis with regional insight to translate ambition into executable strategy.",
      body2: "We do not deliver reports that gather dust. We embed with your team to design, decide and implement, accountable to outcomes, not slides.",
    },
    servicesTitle: "What we do",
    services: [
      { t: "Corporate Strategy", d: "Growth, portfolio and competitive strategy for the long game." },
      { t: "Market Entry", d: "Structured expansion into the GCC and emerging markets." },
      { t: "Operational Excellence", d: "Process, cost and performance transformation that sticks." },
      { t: "Digital Transformation", d: "Technology and data strategy aligned to business value." },
      { t: "Organisation & Change", d: "Structure, talent and culture built for what comes next." },
    ],
    philosophyTitle: "Our Principles",
    philosophy: [
      { n: "01", t: "Insight with rigour", d: "Every recommendation is grounded in evidence, not opinion." },
      { n: "02", t: "Built to implement", d: "We stay until strategy becomes measurable, operational reality." },
      { n: "03", t: "Aligned incentives", d: "Our success is defined by yours, nothing less." },
    ],
    marquee: ["Strategy", "Market Entry", "Operations", "Transformation", "Advisory", "Change"],
    accent: "#3D2680",
  },
};

export const CONTACT_INFO = {
  email: "team@de-jure.me",
  phone: "+971 554548296",
  address: "United Arab Emirates (relocating and will update soon)",
};

// Animated counters, used on the gateway and sub-sites.
export const STATS = [
  { value: 12, suffix: "", label: "Countries we work across" },
  { value: 500, suffix: "+", label: "Clients served" },
  { value: 18, suffix: "", label: "Years of combined experience" },
  { value: 4, suffix: "", label: "Specialised practices" },
];

// Clients-served stat, shown on the group landing page.
export const CLIENTS_STAT = { value: 500, suffix: "+", label: "Clients served worldwide" };

// Countries/regions we operate in.
export const COUNTRIES = [
  "United Arab Emirates", "India", "Saudi Arabia", "Qatar", "Singapore", "United Kingdom",
  "Australia", "China", "Hong Kong", "Brazil", "Bahrain", "Oman",
];

// Leadership profiles (from the De Jure Legal corporate profile).
export const LEADERSHIP = [
  {
    name: "Archit",
    initials: "A",
    photo: "/team/archit.jpg",
    role: "Solicitor of England & Wales",
    email: "ak@de-jure.me",
    experience:
      "8 years of experience as a Solicitor of England and Wales, advising clients across multiple sectors and jurisdictions.",
    education:
      "BSc (Hons) Biomedical Science, University of Essex; Law degree, University of Leicester; LPC & LLM, University of Law, Birmingham.",
    credentials:
      "Extensive work across contracting, construction, oil & gas, commodities, and food & beverage sectors.",
    areas: [
      "Corporate Structuring",
      "M&A Transactions",
      "Corporate Commercial",
      "Shipping & Commodities",
      "Private Client Estate Planning",
      "Real Estate",
    ],
  },
  {
    name: "Priti",
    initials: "P",
    photo: "/team/priti.jpg",
    role: "Legal Consultant, Dubai Legal Affairs",
    email: "pd@de-jure.me",
    experience:
      "Over 20 years of practice, specialising in problem-solving, client advocacy and creative legal strategies.",
    education:
      "B.Com (2003), LL.B. (2006), LL.M. in Business Law (2008), Certified Fraud Examiner (ACFE).",
    credentials:
      "Admitted to the Bar Council of Maharashtra, India, and registered as a Legal Consultant with the Dubai Legal Affairs Department, UAE.",
    areas: ["Family Law", "Real Estate Law", "Litigation", "Private Client"],
  },
];
